import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

export type TrendingRow = {
  label: string;
  sublabel?: string;
  delta?: number;
};

type Props = { rows: TrendingRow[]; emptyLabel?: string };

/** Compact "Trending Topics"-style list with signed delta pills. */
export const TrendingList: React.FC<Props> = ({ rows, emptyLabel = "Nothing to show" }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  if (!rows.length) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", minHeight: 120 }}>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {rows.map((row) => {
        const hasDelta = typeof row.delta === "number";
        const positive = (row.delta ?? 0) >= 0;
        const key = positive ? "success" : "error";
        const main = theme.palette[key].main;
        const text = isLight ? theme.palette[key].dark : theme.palette[key].light;
        return (
          <Stack
            key={row.label}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: "background-color 160ms ease",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary" }}>
                {row.label}
              </Typography>
              {row.sublabel && (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                  {row.sublabel}
                </Typography>
              )}
            </Box>
            {hasDelta && (
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  px: 1,
                  py: 0.4,
                  borderRadius: 1.5,
                  color: text,
                  bgcolor: alpha(main, isLight ? 0.13 : 0.2),
                  whiteSpace: "nowrap",
                }}
              >
                {positive ? "↗" : "↘"} {positive ? "+" : ""}
                {row.delta}
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
